import React, { useEffect, useId, useRef } from 'react';

export interface ConfirmDialogProps {
  title: string;
  message: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  disabled?: boolean;
  error?: string | null;
  /** What the action changes, one sentence each; shown as a list before the buttons (PRODUCT.md principle 4). */
  consequences?: React.ReactNode[];
  /** 'danger' for interventions in the grid: red confirm button, initial focus on cancel. */
  tone?: 'default' | 'danger';
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href]';

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel = "Bestätigen",
  cancelLabel = "Abbrechen",
  disabled = false,
  error = null,
  consequences,
  tone = 'default',
}) => {
  const id = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const danger = tone === 'danger';

  // Focus the safe choice for interventions, give focus back to the trigger on close
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    (danger ? cancelRef : confirmRef).current?.focus();
    return () => previous?.focus?.();
  }, [danger]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !disabled) {
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key !== 'Tab' || !panelRef.current) return;
    const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 bg-primary-900/60 dark:bg-black/70 flex items-center justify-center z-2000" onKeyDown={onKeyDown}>
      <div
        ref={panelRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-message`}
        className="bg-white dark:bg-primary-800 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl border border-gray-200 dark:border-primary-700"
      >
        <h3 id={`${id}-title`} className="text-lg font-semibold mb-2 text-gray-900 dark:text-primary-50">{title}</h3>
        <div id={`${id}-message`} className="text-sm text-gray-700 dark:text-primary-200 mb-4">{message}</div>
        {consequences && consequences.length > 0 && (
          <ul className="mb-5 space-y-1.5 text-sm text-gray-700 dark:text-primary-200">
            {consequences.map((c, i) => (
              <li key={i} className="flex gap-2">
                <span aria-hidden="true" className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${danger ? 'bg-error-500' : 'bg-accent-primary'}`} />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        )}
        {error && (
          <div role="alert" className="mb-4 p-2 text-sm bg-error-100 dark:bg-error-900/40 border border-error-300 dark:border-error-700 text-error-700 dark:text-error-200 rounded">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            ref={cancelRef}
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 dark:text-primary-200 hover:text-gray-900 dark:hover:text-primary-50 bg-gray-100 dark:bg-primary-700 hover:bg-gray-200 dark:hover:bg-primary-600 rounded transition-colors"
            disabled={disabled}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            className={`px-4 py-2 font-medium text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              danger
                ? 'bg-error-600 hover:bg-error-700 active:bg-error-800'
                : 'bg-accent-primary hover:bg-accent-primary-hover active:bg-accent-primary-active'
            }`}
            disabled={disabled}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
